import React, {useState, useEffect, useCallback, ChangeEvent, FormEvent, useRef} from 'react';
import {collection, getDocs, query, doc, getDoc, onSnapshot} from 'firebase/firestore';
import {auth, db} from '../../../../firebase'; // Adjust path as per your project
import { PanelEmailData } from '../contexts/EmailPanelContext'; // For initialData type
import { MultiValue, OnChangeValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FirebaseUploadAdapterPlugin } from '../../../firebase/FirebaseUploadAdapter'; // Adjust path as necessary
import { fixImageStyles } from '../../../modules/accounts/components/settings/modals/SignatureEditModal'; // Import the shared function
import { toast } from 'react-toastify'; // <-- Import react-toastify
import { KTSVG } from '../../../../_metronic/helpers';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { useEmailPanel } from '../contexts/EmailPanelContext'; // Import email panel context for draft operations
import {
  CLASSIC_INLINE_MAX_TOTAL_BYTES,
  FORWARD_IMAGE_MAX_BYTES,
  attachmentBytes,
  checkCanAdd,
  formatBytes,
  formatLimit,
  formatTotalIndicator,
  removeCidImages,
  resolveForwardCidImages,
  sumAttachmentBytes,
} from '../utils/attachmentLimits';
import { fetchGraphAttachmentsList, resolveInlineGraphImages } from '../utils/inlineImageResolver';
import { replyRecipients } from '../utils/replyRecipients';
import { DEFAULT_FEED_MAILBOXES } from '../utils/feedWindow';

// Utility functions to handle reply and forward subjects properly
const formatReplySubject = (originalSubject: string | undefined): string => {
  if (!originalSubject) return 'Re: ';
  
  // Check if subject already starts with "Re:" (case insensitive)
  const trimmedSubject = originalSubject.trim();
  if (trimmedSubject.toLowerCase().startsWith('re:')) {
    return trimmedSubject; // Return as-is if already has Re:
  }
  
  return `Re: ${trimmedSubject}`;
};

const formatForwardSubject = (originalSubject: string | undefined): string => {
  if (!originalSubject) return 'Fwd: ';
  
  // Check if subject already starts with "Fwd:" or "FWD:" (case insensitive)
  const trimmedSubject = originalSubject.trim();
  if (trimmedSubject.toLowerCase().startsWith('fwd:')) {
    return trimmedSubject; // Return as-is if already has Fwd:
  }
  
  return `Fwd: ${trimmedSubject}`;
};

// Utility function to clean HTML content for replies/forwards.
//
// cid: images: a quoted <img src="cid:..."> points at an attachment of the
// ORIGINAL message and can never resolve in a new one - the recipient (and the
// quote preview) would get a broken-image box. This function deliberately
// keeps them so a FORWARD can first resolve the small ones to data: URLs
// (resolveForwardCidImages); every caller must then pass the result through
// removeCidImages (replies straight away, forwards after that attempt).
const cleanEmailHtmlForReply = (htmlContent: string, enableCleaning: boolean = true): string => {
  if (!htmlContent) return '';

  // Option to disable the cosmetic Office-CSS cleaning, but NEVER skip the
  // security sanitizer — this is inbound, attacker-controllable email HTML.
  if (!enableCleaning) {
    return sanitizeHtml(htmlContent);
  }

  // Very conservative cleaning - only remove the specific CSS blocks that cause problems
  // Focus on the exact issue: Microsoft Office CSS in <style> tags
  let cleaned = htmlContent
    // Remove only the problematic style blocks with Office CSS
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    // Remove XML declarations
    .replace(/<\?xml[^>]*\?>/gi, '')
    // Remove Office namespace tags while preserving content
    .replace(/<o:p>/gi, '')
    .replace(/<\/o:p>/gi, '')
    // Remove Word-specific class attributes but keep the HTML elements
    .replace(/\sclass="Mso[^"]*"/gi, '')
    .replace(/\sclass="x_mso[^"]*"/gi, '')
    // Clean up excessive whitespace but preserve structure
    .replace(/\s{2,}/g, ' ')
    .trim();

  // Fall back to the original if cosmetic cleaning removed too much.
  if (!cleaned.trim() || cleaned.length < htmlContent.length * 0.1) {
    console.warn('[cleanEmailHtmlForReply] Cleaning removed too much content, returning original');
    cleaned = htmlContent;
  }

  // ALWAYS sanitize before returning — the result is rendered in our DOM
  // (quoted-thread preview) and concatenated into the outgoing body.
  return sanitizeHtml(cleaned);
};

interface SenderAccount {
  id: string; 
  accountName: string;
  signatureHtml?: string;
}

// Contract B shape. This composer only ever CREATES inline base64 items, but a
// draft opened here may carry items that only have a Storage ref (made by the
// terminal composer) or whose bytes the draft size guard dropped - those must
// render and pass through without a `content` (never .length on undefined).
interface AttachmentFile {
  filename: string;
  content?: string; // Base64 encoded (small / inline files)
  contentType?: string;
  size?: number;
  storagePath?: string; // Cloud Storage ref (large files) - passed through untouched
}

interface EmailOptionType {
  value: string;
  label: string;
}

// Who is "us" when a reply is pre-filled. replyRecipients takes separate answers, and blurring
// them is how a colleague gets silently dropped from Reply-all's Cc:
//   selfAddresses    the signed-in person ONLY. "Us" for FILTERING (never Cc'd, avoided in To) and
//                    mail they authored is theirs wherever its copy sits. The email's own mailbox
//                    is added as "us" by the helper itself. Ben and Lidia are two people sharing
//                    the business: when a client wrote to both, Ben's reply-all keeps lidia@ on Cc.
//   accountAddresses every send account + feed mailbox: authorship evidence ONLY for legacy docs
//                    that carry no mailbox. Never used for filtering.
// The From dropdown is deliberately NOT part of "us": the prefill runs before the accounts have
// loaded (empty on a fresh mount) and, when the composer is reused for the next reply, still shows
// the PREVIOUS email's From - lidia@ there would silently drop her from this reply's Cc. The From
// that matters is the mailbox the original arrived on, which the helper already covers.
function lowerAddresses(values: ReadonlyArray<unknown>): string[] {
  const out: string[] = [];
  values.forEach((v) => { if (typeof v === 'string' && v.trim()) out.push(v.trim().toLowerCase()); });
  return out;
}

function replyAddressSets(accounts: ReadonlyArray<{ id: string }>) {
  return {
    selfAddresses: lowerAddresses([auth?.currentUser?.email]),
    accountAddresses: lowerAddresses([...accounts.map((a) => a.id), ...DEFAULT_FEED_MAILBOXES]),
  };
}

interface SendEmailFormProps {
  initialData?: PanelEmailData | null;
  onSendSuccess?: () => void;
  onBeforeClose?: () => Promise<boolean> | boolean; // Return true to allow close, false to prevent
}

interface SendEmailFormRef {
  checkForUnsavedChanges: () => boolean;
  hasUnsavedChanges: boolean;
  /** The Firestore draft ID currently loaded in the composer, or null if none. */
  currentDraftId: string | null;
  /** Trigger a manual draft save. Resolves once the save completes. */
  triggerSave: () => Promise<void>;
  /** Prepare the form for discard: deletes the Firestore draft (if any),
   *  clears the form, and prevents the save-on-unmount from re-saving. */
  prepareForDiscard: () => Promise<void>;
}

// Re-export the ref type so parents can reference it without coupling to internals
export type { SendEmailFormRef };

const SendEmailForm = React.forwardRef<SendEmailFormRef, SendEmailFormProps>(
  ({ initialData, onSendSuccess, onBeforeClose }, ref) => {
  const [senderAccounts, setSenderAccounts] = useState<SenderAccount[]>([]);
  // Mirror read by the reply prefill effect, which must see the account list as it is NOW.
  const senderAccountsRef = useRef<SenderAccount[]>([]);
  senderAccountsRef.current = senderAccounts;
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedTo, setSelectedTo] = useState<MultiValue<EmailOptionType>>([]);
  const [selectedCc, setSelectedCc] = useState<MultiValue<EmailOptionType>>([]);
  const [selectedBcc, setSelectedBcc] = useState<MultiValue<EmailOptionType>>([]);
  const [subject, setSubject] = useState<string>('');
  const [htmlBody, setHtmlBody] = useState<string>('');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]); // For new attachments

  // -- Forward: keep the original's attachments ------------------------------
  // The server carries them over (createForward, or a copy when the From
  // mailbox differs from the original's). This composer only remembers WHICH
  // message, lists the files as chips and reports the ones the user removed -
  // without this a forward went out silently minus the original's files.
  const [forwardSource, setForwardSource] = useState<{ emailId: string; graphId: string } | null>(null);
  const forwardTokenRef = useRef<number>(0); // drops stale attachment-list responses
  const [forwardOriginals, setForwardOriginals] = useState<Array<{ name: string; size: number | null }>>([]);
  const [forwardOriginalsStatus, setForwardOriginalsStatus] = useState<'none' | 'loading' | 'loaded' | 'unavailable'>('none');
  const [forwardExcluded, setForwardExcluded] = useState<string[]>([]);
  const keptForwardOriginals = forwardOriginals.filter((o) => !forwardExcluded.includes(o.name));

  // MUST run whenever the composition changes identity (new reply/compose, a
  // draft opened, sent, discarded): a stale source would make the NEXT,
  // unrelated email carry this original's attachments.
  const resetForwardState = useCallback(() => {
    forwardTokenRef.current += 1;
    setForwardSource(null);
    setForwardOriginals([]);
    setForwardOriginalsStatus('none');
    setForwardExcluded([]);
  }, []);

  const beginForward = useCallback((emailId: string, graphId: string) => {
    const token = ++forwardTokenRef.current;
    setForwardExcluded([]);
    setForwardOriginals([]);
    if (!emailId && !graphId) {
      setForwardSource(null);
      setForwardOriginalsStatus('none');
      return;
    }
    setForwardSource({ emailId, graphId });
    if (!emailId) { setForwardOriginalsStatus('unavailable'); return; }
    setForwardOriginalsStatus('loading');
    fetchGraphAttachmentsList(emailId)
      .then((list) => {
        if (token !== forwardTokenRef.current) return;
        setForwardOriginals(
          list.filter((a) => !a.isInline && a.filename)
            .map((a) => ({ name: a.filename, size: typeof a.size === 'number' ? a.size : null })),
        );
        setForwardOriginalsStatus('loaded');
      })
      .catch(() => {
        if (token !== forwardTokenRef.current) return;
        setForwardOriginals([]);
        setForwardOriginalsStatus('unavailable'); // never blocks the forward
      });
  }, []);

  const excludeForwardOriginal = (name: string) =>
    setForwardExcluded((prev) => (prev.includes(name) ? prev : [...prev, name]));
  const [signatureHtml, setSignatureHtml] = useState<string>('');
  const [clientEmailOptions, setClientEmailOptions] = useState<EmailOptionType[]>([]); // For react-select options
  const [isFetchingClients, setIsFetchingClients] = useState(false); // Loading state for client emails
  const [defaultReplyAccountId, setDefaultReplyAccountId] = useState<string | null>(null); // User's default reply account

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentAppTheme, setCurrentAppTheme] = useState('light');
  const ckeditorRef = useRef<any>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // Auto-save related state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [lastSavedContent, setLastSavedContent] = useState<string>('');
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to hold the latest performAutoSave without re-creating the interval (Bug 1 fix)
  const performAutoSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Ref to track when the last auto-save completed, used to avoid interval+debounce double-saves (Bug 2 fix)
  const lastAutoSaveRef = useRef<number>(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  // Live-update: when Andrei edits the open draft server-side, this holds the
  // incoming version when the editor has UNSAVED edits (we never clobber the
  // user's typing — we offer a "Load update" banner instead). When the editor
  // is clean, the change is applied silently. Refs let the onSnapshot closure
  // read the latest body without re-subscribing each keystroke.
  const [pendingExternalUpdate, setPendingExternalUpdate] = useState<any | null>(null);
  const lastSavedContentRef = useRef<string>('');
  useEffect(() => { lastSavedContentRef.current = lastSavedContent; }, [lastSavedContent]);
  const lastSelfWriteAtRef = useRef<number>(0); // when WE last saved (suppresses our own echo)
  // Ref mirror of htmlBody so the unmount-cleanup closure can read the latest value
  const htmlBodyRef = useRef<string>('');
  // Ref mirror of attachment filenames so the draft live-sub can detect an
  // external attachment change (e.g. Andrei attaching an invoice PDF).
  const attachmentSigRef = useRef<string>('');

  // Get draft operations from context
  const { saveDraft, updateDraft, deleteDraft: deleteDraftFromContext, currentDraft, draftMode } = useEmailPanel();

  /** Delete the current draft from Firestore, clear form, and prevent save-on-unmount. */
  const prepareForDiscard = async () => {
    if (currentDraftId) {
      try {
        await deleteDraftFromContext(currentDraftId);
      } catch (err) {
        console.warn('[SendEmailForm] Failed to delete draft on discard:', err);
      }
    }
    // Clear form content so save-on-unmount finds nothing to persist
    setHtmlBody('');
    htmlBodyRef.current = '';
    resetForwardState();
    setHasUnsavedChanges(false);
    setCurrentDraftId(null);
    setLastSavedContent('');
    setAutoSaveStatus('idle');
  };

  // Expose functions to parent component
  React.useImperativeHandle(ref, () => ({
    checkForUnsavedChanges,
    hasUnsavedChanges,
    currentDraftId,
    triggerSave: triggerManualSave,
    prepareForDiscard,
  }), [hasUnsavedChanges, htmlBody, draftMode, currentDraftId]);

  // New: manage quoted thread outside of CKEditor to avoid heavy initial load
  const [quotedHtml, setQuotedHtml] = useState<string>('');
  const [includeQuotedThread, setIncludeQuotedThread] = useState<boolean>(true);
  const [showQuotedPreview, setShowQuotedPreview] = useState<boolean>(false);

  // CC/BCC visibility: collapsed by default; auto-expand when fields have values
  const [showCcBcc, setShowCcBcc] = useState<boolean>(false);

  // Auto-expand CC/BCC when they have values (e.g. Reply All pre-fills CC)
  useEffect(() => {
    if (selectedCc.length > 0 || selectedBcc.length > 0) {
      setShowCcBcc(true);
    }
  }, [selectedCc, selectedBcc]);

  // Auto-save functions
  const hasContentChanged = (currentContent: string) => {
    return currentContent !== lastSavedContent && currentContent.trim().length > 0;
  };

  const getCurrentFormData = () => {
    return {
      subject,
      htmlBody,
      to: selectedTo.map(opt => opt.value),
      cc: selectedCc.map(opt => opt.value),
      bcc: selectedBcc.map(opt => opt.value),
      attachments,
      senderAccountId: selectedAccountId,
      // A resumed forward draft has no initialData (the panel passes {}) - keep
      // its forward link so the NEXT resume still knows to keep the originals.
      replyToEmailId: initialData?.id ?? (forwardSource?.emailId || undefined),
      isReply: initialData?.isReply || false,
      isForward: initialData?.isForward || !!forwardSource,
    };
  };

  const performAutoSave = async () => {
    if (!autoSaveEnabled || autoSaveStatus === 'saving') return;
    // A send in flight owns the content — saving now would re-create the
    // draft doc after the send's cleanup deleted it (ghost drafts of sent
    // emails). The 30s interval / next edit re-arms saving on send failure.
    if (isLoading) return;

    const currentContent = htmlBody;
    if (!hasContentChanged(currentContent)) {
      setHasUnsavedChanges(false);
      return;
    }

    try {
      setAutoSaveStatus('saving');
      setHasUnsavedChanges(true);

      const formData = getCurrentFormData();

      let draftId: string;
      if (currentDraftId) {
        // Update existing draft
        await updateDraft(currentDraftId, formData);
        draftId = currentDraftId;
      } else {
        // Create new draft
        draftId = await saveDraft(formData);
        setCurrentDraftId(draftId);
      }

      setLastSavedContent(currentContent);
      lastAutoSaveRef.current = Date.now(); // Bug 2: record time of successful save
      lastSelfWriteAtRef.current = Date.now(); // mark our write so its echo is ignored
      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);

      // Reset saved status after 3 seconds
      setTimeout(() => {
        setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
      }, 3000);

    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');

      // Reset error status after 5 seconds
      setTimeout(() => {
        setAutoSaveStatus(prev => prev === 'error' ? 'idle' : prev);
      }, 5000);
    }
  };

  // Bug 1: Keep the ref in sync with the latest performAutoSave closure on every render
  performAutoSaveRef.current = performAutoSave;
  // Keep htmlBody ref in sync so the unmount-cleanup closure can read the latest value
  htmlBodyRef.current = htmlBody;
  attachmentSigRef.current = (attachments || []).map((a) => a.filename).sort().join('|');

  const debouncedAutoSave = () => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (3 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      lastAutoSaveRef.current = Date.now(); // mark as triggered to suppress near-simultaneous interval (Bug 2)
      performAutoSaveRef.current();
    }, 3000);
  };

  const triggerManualSave = async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    await performAutoSave();
  };

  // Cleanup timeout on unmount + fire-and-forget final save
  React.useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      // Safety net: if the component is about to unmount with unsaved content,
      // fire a last-ditch save so the user's work isn't lost on modal close
      // or page navigation. The save is fire-and-forget — we can't await it.
      if (htmlBodyRef.current?.trim()) {
        try { performAutoSaveRef.current(); } catch { /* best effort */ }
      }
    };
  }, []);

  // Interval-based auto-save every 30 seconds while composing.
  // Uses performAutoSaveRef so the interval is never re-created on state changes (Bug 1).
  // Skips if a debounce save happened within the last 5 seconds (Bug 2).
  React.useEffect(() => {
    // NOTE: do NOT gate on draftMode — compose/reply/forward open with
    // draftMode 'none' (only the Drafts list sets it), which used to silently
    // disable autosave, the save indicator, the unsaved-changes prompt, and the
    // beforeunload guard for the primary compose flows. Key off content instead.
    if (!autoSaveEnabled) return;
    const intervalId = setInterval(() => {
      if (Date.now() - lastAutoSaveRef.current < 5000) return; // too recent, skip
      performAutoSaveRef.current();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [autoSaveEnabled]);

  // Check for unsaved changes when component unmounts or when trying to close
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && htmlBody.trim()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, htmlBody]);

  const handleDiscardChanges = async () => {
    setShowUnsavedChangesModal(false);

    // Actually DELETE the autosaved Firestore draft + suppress save-on-unmount
    // (prepareForDiscard handles draft delete, htmlBody clear, flags) — otherwise
    // "Discard" leaves an orphan draft in the Drafts panel.
    await prepareForDiscard();

    // Clear the remaining form fields prepareForDiscard doesn't touch.
    setSelectedTo([]);
    setSelectedCc([]);
    setSelectedBcc([]);
    setSubject('');
    setQuotedHtml('');
    setIncludeQuotedThread(false);
    setAttachments([]);

    if (ckeditorRef.current) {
      ckeditorRef.current.setData('');
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Honour the abandon intent: close the composer (same callback as send).
    onSendSuccess?.();
  };

  const handleSaveBeforeClose = async () => {
    try {
      await triggerManualSave();
      setShowUnsavedChangesModal(false);
      // After saving, the parent component should handle closing
    } catch (error) {
      console.error('Failed to save before close:', error);
    }
  };

  const checkForUnsavedChanges = () => {
    // Guard removed: draftMode !== 'none' was preventing the workspace from detecting
    // unsaved changes since the workspace historically never set draftMode. Now we check
    // purely on whether the user has typed content worth saving.
    if (hasUnsavedChanges && htmlBody.trim()) {
      setShowUnsavedChangesModal(true);
      return true; // Has unsaved changes
    }
    return false; // No unsaved changes
  };

  useEffect(() => {
    // Delay rendering of CKEditor to allow parent animations to complete
    const timer = setTimeout(() => {
        setIsEditorReady(true);
    }, 450); // Match the transition time of the sliding panel

    return () => clearTimeout(timer);
  }, []);

  // Fetch user's default reply account preference
  useEffect(() => {
    const fetchUserDefaultReplyAccount = async () => {
      try {
        const userId = localStorage.getItem('user_uid');
        if (userId) {
          const userRef = doc(db, "users", userId);
          const docSnap = await getDoc(userRef);
          if (docSnap.exists()) {
            const userData = docSnap.data();
            setDefaultReplyAccountId(userData.defaultReplyAccountId || null);
          }
        }
      } catch (err: any) {
        console.error("Error fetching user default reply account:", err);
      }
    };
    fetchUserDefaultReplyAccount();
  }, []);

  useEffect(() => {
    const fetchEnabledAccounts = async () => {
      setIsFetchingAccounts(true);
      try {
        // Fetch Graph mail accounts via HTTP endpoint (callable functions are blocked at the edge)
        const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/getGraphMailAccountsHttp', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({}),
        });
        const data = (await resp.json()) as {
          ok: boolean;
          accounts: Array<{id: string; mailbox: string; displayName: string; signatureHtml: string; isEnabled: boolean}>;
        };
        
        if (!data.ok) {
          throw new Error('Failed to fetch Graph mail accounts');
        }
        
        // Filter to only enabled accounts and map to SenderAccount format
        const fetchedAccounts = data.accounts
          .filter(acc => acc.isEnabled)
          .map(acc => ({
            id: acc.id, // This is the mailbox email address
            accountName: acc.displayName || acc.mailbox,
            signatureHtml: acc.signatureHtml || '',
          } as SenderAccount));
        
        setSenderAccounts(fetchedAccounts);
        
        if (fetchedAccounts.length > 0) {
          let accountToSelect = fetchedAccounts[0].id;

          if (initialData?.isReply || (initialData as any)?.isReplyAll || initialData?.isForward) {
            // Prefer the mailbox the original message was RECEIVED on, so the
            // reply goes out from the same address the customer wrote to (not
            // some other mailbox / a stale default). Account id IS the mailbox
            // address. Fall back to the user's default reply account, then the
            // first account, only when the receiving mailbox isn't sendable.
            const recvBox = String((initialData as any)?.mailbox || '').toLowerCase().trim();
            const recvAccount = recvBox ? fetchedAccounts.find(acc => acc.id.toLowerCase() === recvBox) : undefined;
            if (recvAccount) {
              accountToSelect = recvAccount.id;
            } else if (defaultReplyAccountId) {
              const defaultAccount = fetchedAccounts.find(acc => acc.id === defaultReplyAccountId);
              if (defaultAccount) accountToSelect = defaultReplyAccountId;
            }
          }

          setSelectedAccountId(accountToSelect);
          const selectedAccount = fetchedAccounts.find(acc => acc.id === accountToSelect);
          setSignatureHtml(selectedAccount?.signatureHtml || '');
        }
      } catch (err: any) {
        console.error("Error fetching Graph mail accounts:", err);
        setError(`Failed to fetch sender accounts: ${err.message}`);
      }
      setIsFetchingAccounts(false);
    };
    fetchEnabledAccounts();
  }, [defaultReplyAccountId, initialData]);

  useEffect(() => {
    const fetchClientEmails = async () => {
      setIsFetchingClients(true);
      try {
        const customersCollectionRef = collection(db, "customers");
        const q = query(customersCollectionRef);
        const querySnapshot = await getDocs(q);
        
        const emailOptionList: EmailOptionType[] = [];
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Add top-level customer email with name
          if (data.email && typeof data.email === 'string' && data.email.trim() !== '') {
            const label = data.name ? `${data.name} <${data.email.trim()}>` : data.email.trim();
            emailOptionList.push({ value: data.email.trim(), label });
          }
          // Add emails from the contacts array with contact name
          if (Array.isArray(data.contacts)) {
            data.contacts.forEach((contact: any) => {
              if (contact && contact.email && typeof contact.email === 'string' && contact.email.trim() !== '') {
                const contactName = contact.name || 'Contact'; // Fallback if contact name is missing
                const label = `${contactName} <${contact.email.trim()}>`;
                emailOptionList.push({ value: contact.email.trim(), label });
              }
            });
          }
        });
        
        // Filter for unique options based on value (email address)
        const uniqueEmailOptions = Array.from(new Map(emailOptionList.map(item => [item.value, item])).values());
        setClientEmailOptions(uniqueEmailOptions);
      } catch (err: any) {
        console.error("Error fetching client emails from 'customers' collection:", err);
      }
      setIsFetchingClients(false);
    };
    fetchClientEmails();
  }, []);

  useEffect(() => {
    // Opening a saved DRAFT: the draftMode='edit' hydration (below) owns the
    // form fields. The panel sets `initialData` (its stale currentEmailData/{})
    // in a LATER tick than setDraftMode, so without this guard THIS effect runs
    // after hydration and overwrites the draft body with empty — the "have to
    // click twice to populate" bug. In edit mode, leave the fields to the draft.
    if (draftMode === 'edit') return;
    // Every branch below starts a NEW composition: drop the previous one's
    // forward link (the forward branch re-arms it).
    resetForwardState();
    if (initialData) {
      if (initialData.isReply || initialData.isReplyAll) {
        // Who a reply addresses is decided by replyRecipients (one rule set for every reply
        // entry point), fed the ORIGINAL email untouched: mail the owner SENT continues with
        // its recipients - never the owner's own address - and mail from a client goes to
        // that client. Reply-all carries the remaining participants into cc (never the
        // owner's own mailboxes). "Sent by the owner" is read from the folder, type,
        // direction, mailbox and sender, not folder alone.
        const rr = replyRecipients(initialData, {
          mode: initialData.isReplyAll ? 'replyAll' : 'reply',
          ...replyAddressSets(senderAccountsRef.current),
        });
        // Chip labels: the display name the original email carried, else toEmailOptions' fallback.
        const named = (list: string[]) => toEmailOptions(list, list.map(a => rr.names[a.toLowerCase()] || ''));
        setSelectedTo(named(rr.to));
        setSelectedCc(named(rr.cc));
        setSelectedBcc([]);
        setSubject(formatReplySubject(initialData.subject));
        // A reply never resolves the original's cid: images - drop them.
        const originalBody = removeCidImages(cleanEmailHtmlForReply(initialData.html || initialData.text || ''));
        const fromLine = initialData.from?.map(f => f.name || f.address).join(', ') || 'Original Sender';
        const dateLine = initialData.date ? `On ${new Date(initialData.date.seconds * 1000).toLocaleString()}` : 'Previously';
        // Keep the heavy quoted content out of the editor; store separately
        setQuotedHtml(`
<hr>
<p><em>${dateLine}, ${fromLine} wrote:</em></p>
<blockquote>${originalBody}</blockquote>
        `.trim());
        setIncludeQuotedThread(true);
        setHtmlBody(''); // Start with an empty editor for fast typing
        setAttachments([]); // Replies usually don't carry over attachments by default
      } else if (initialData.isForward) {
        setSelectedTo([]);
        setSelectedCc([]);
        setSelectedBcc([]);
        setSubject(formatForwardSubject(initialData.subject));
        // Keep cid: refs for now so the small images can be resolved to data:
        // URLs below; the quote is built from the cid-free copy, so a dangling
        // cid: can never reach the preview or the outgoing body.
        const originalWithCid = cleanEmailHtmlForReply(initialData.html || initialData.text || '');
        const originalBody = removeCidImages(originalWithCid);
        const fromLine = initialData.from?.map(f => f.name || f.address).join(', ') || 'Original Sender';
        const originalToLine = Array.isArray(initialData.to) ? initialData.to.join(', ') : initialData.to;
        const dateLine = initialData.date ? `Sent: ${new Date(initialData.date.seconds * 1000).toLocaleString()}` : '';

        // A function so the async image upgrade below can rebuild the WHOLE
        // quote around a better original.
        const buildForwardQuote = (orig: string) => `
<p><em>-------- Forwarded Message --------</em><br>
<em>From: ${fromLine}</em><br>
<em>Date: ${dateLine}</em><br>
<em>Subject: ${initialData.subject || ''}</em><br>
<em>To: ${originalToLine || ''}</em></p>
<br>
${orig}
        `.trim();
        const quoted = buildForwardQuote(originalBody);
        setQuotedHtml(quoted);
        setIncludeQuotedThread(true);
        setHtmlBody(''); // Keep editor light for forwards too
        // The composer's own attachments start empty (the user adds more); the
        // ORIGINAL's files are not copied into it - the server re-attaches them
        // from the source message (forwardFromEmailId on send), and they are
        // listed as chips so the owner can see / leave out what travels.
        setAttachments([]);
        const forwardEmailId = String((initialData as any).emailId || initialData.id || '');
        beginForward(forwardEmailId, String(initialData.graph?.id || ''));
        // Small cid: images -> data: URLs so the quoted forward still shows
        // them (the server's data:->cid conversion re-embeds them on send).
        // Anything unresolvable or over budget is stripped, never left
        // dangling. Only attachments within the image budget are worth
        // downloading - a bigger one would be dropped afterwards anyway.
        if (forwardEmailId && originalWithCid !== originalBody) {
          void resolveForwardCidImages(originalWithCid, async (h) => {
            const list = await fetchGraphAttachmentsList(forwardEmailId);
            const small = list.filter((a) => !(typeof a.size === 'number' && a.size > FORWARD_IMAGE_MAX_BYTES));
            if (small.length === 0) return h;
            return resolveInlineGraphImages(forwardEmailId, h, { attachments: small });
          }).then((better) => {
            if (!better || better === originalBody) return;
            // Re-sanitize: the quote preview and the outgoing body trust it.
            // Swap only while the quote is still exactly ours (the composer
            // may have moved on to another message meanwhile).
            const next = buildForwardQuote(sanitizeHtml(better));
            setQuotedHtml((prev) => (prev === quoted ? next : prev));
          }).catch(() => { /* the images stay stripped */ });
        }
      } else {
        // Default compose mode (not reply/forward, but potentially with pre-filled 'to' from a contact)
        setSelectedTo(toEmailOptions(initialData.to));
        setSelectedCc(toEmailOptions(initialData.cc));
        setSelectedBcc(toEmailOptions(initialData.bcc));
        setSubject(initialData.subject || '');
        setHtmlBody(initialData.html || ''); 
        setQuotedHtml('');
        setIncludeQuotedThread(false);
        setAttachments([]);
      }
    } else {
        // Fresh compose
        setSelectedTo([]); setSelectedCc([]); setSelectedBcc([]); setSubject(''); 
        setHtmlBody(''); 
        setQuotedHtml('');
        setIncludeQuotedThread(false);
        setAttachments([]);
    }
    // resetForwardState / beginForward are stable (useCallback, no deps).
  }, [initialData, resetForwardState, beginForward]);

  // When sender account changes, update signatureHtml
  useEffect(() => {
    const acc = senderAccounts.find(a => a.id === selectedAccountId);
    setSignatureHtml(acc?.signatureHtml || '');
  }, [selectedAccountId, senderAccounts]);

  // Auto-insert signature for fresh compose emails when signature becomes available
  useEffect(() => {
    // Only auto-insert for fresh compose (no initialData or initialData without reply/forward flags)
    const isFreshCompose = !initialData || (!initialData.isReply && !initialData.isForward && !initialData.html);

    if (isFreshCompose && signatureHtml && !htmlBody.includes(signatureHtml)) {
      // Auto-insert signature for new emails
      setHtmlBody(autoInsertSignature(htmlBody));
    }
  }, [signatureHtml]);

  // Initialize component with existing draft data when in edit mode
  useEffect(() => {
    if (draftMode === 'edit' && currentDraft) {
      // Outlook text-only drafts sync with htmlBody EMPTY and the content in
      // textBody — render it as simple HTML so the draft doesn't open blank.
      const body = currentDraft.htmlBody
        || (currentDraft.textBody
              ? `<div>${currentDraft.textBody
                    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    .replace(/\r?\n/g, '<br>')}</div>`
              : '');
      setCurrentDraftId(currentDraft.id ?? null);
      setLastSavedContent(body);

      // Populate form with draft data (unconditional — switching drafts must
      // not carry a previous draft's field over an empty one)
      setSubject(currentDraft.subject || '');
      setHtmlBody(body);
      setSelectedTo(toEmailOptions(currentDraft.to || []));
      setSelectedCc(toEmailOptions(currentDraft.cc || []));
      setSelectedBcc(toEmailOptions(currentDraft.bcc || []));
      setAttachments(currentDraft.attachments || []);
      // A resumed FORWARD draft still owes the original's attachments (the
      // initialData effect is skipped in edit mode, so restore the link here).
      resetForwardState();
      if (currentDraft.isForward && currentDraft.replyToEmailId) {
        beginForward(String(currentDraft.replyToEmailId), '');
      }
      if (currentDraft.senderAccountId) setSelectedAccountId(currentDraft.senderAccountId);

      setAutoSaveStatus('idle');
      setHasUnsavedChanges(false);
    }
  }, [draftMode, currentDraft, resetForwardState, beginForward]);

  // ── Live draft updates ────────────────────────────────────────────────
  // Apply an incoming server version of the draft (e.g. Andrei edited it or
  // attached an invoice) into the open editor. Sets lastSavedContent to match
  // so the autosave effect sees no change and doesn't write it straight back.
  const applyExternalDraft = (d: any) => {
    setSubject(d.subject || '');
    setHtmlBody(d.htmlBody || '');
    setSelectedTo(toEmailOptions(Array.isArray(d.to) ? d.to : []));
    setSelectedCc(toEmailOptions(Array.isArray(d.cc) ? d.cc : []));
    setSelectedBcc(toEmailOptions(Array.isArray(d.bcc) ? d.bcc : []));
    setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
    if (d.senderAccountId) setSelectedAccountId(d.senderAccountId);
    setLastSavedContent(d.htmlBody || '');
    setHasUnsavedChanges(false);
    setPendingExternalUpdate(null);
  };

  // Subscribe to the open draft so a server-side edit (Andrei) shows up without
  // a manual reload. A snapshot is "external" only when its htmlBody differs
  // from BOTH the editor's current body and our last-saved body — that excludes
  // our own autosave echo AND the user's in-progress (not-yet-saved) typing.
  // Clean editor → apply silently; unsaved edits → stash + show a banner so the
  // user's work is never clobbered.
  useEffect(() => {
    if (!currentDraftId) return;
    const unsub = onSnapshot(doc(db, 'email_drafts', currentDraftId), (snap) => {
      if (!snap.exists()) return;
      // Ignore the echo of OUR OWN writes. hasPendingWrites catches the local
      // optimistic write; the server-ack echo (updatedAt serverTimestamp
      // resolving) arrives a beat later with hasPendingWrites=false, so ALSO
      // suppress anything within a short window of our own save — without this,
      // that echo re-set the editor mid-type and jumped the caret to the end.
      // Andrei's edits while the draft sits idle (no recent self-write) still
      // surface the banner.
      if (snap.metadata.hasPendingWrites) return;
      if (Date.now() - lastSelfWriteAtRef.current < 3000) return;
      const d: any = { id: snap.id, ...snap.data() };
      if (d.status && d.status !== 'draft') return; // sent/deleted — ignore
      const incoming = String(d.htmlBody || '');
      const bodyExternal = incoming !== htmlBodyRef.current && incoming !== lastSavedContentRef.current;
      // Also catch attachment-only changes (e.g. Andrei attached an invoice PDF
      // without touching the body).
      const incomingAttachSig = (Array.isArray(d.attachments) ? d.attachments : []).map((a: any) => a?.filename).filter(Boolean).sort().join('|');
      const attachExternal = incomingAttachSig !== attachmentSigRef.current;
      if (!bodyExternal && !attachExternal) return;
      const hasUnsaved = htmlBodyRef.current !== lastSavedContentRef.current;
      if (hasUnsaved) {
        setPendingExternalUpdate(d); // don't clobber unsaved typing — offer it
      } else {
        applyExternalDraft(d);
      }
    }, (err) => { console.warn('[SendEmailForm] draft live-sub error:', err); });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDraftId]);

  // Trigger auto-save when content changes (keyed on content, not draftMode —
  // see the interval-autosave note above).
  useEffect(() => {
    if (htmlBody && autoSaveEnabled) {
      debouncedAutoSave();
    }
  }, [htmlBody, subject, selectedTo, selectedCc, selectedBcc, selectedAccountId]);

  useEffect(() => {
    const updateTheme = () => {
      const theme = document.documentElement.getAttribute('data-bs-theme') || 'light';
      setCurrentAppTheme(theme);
    }
    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);

  const getFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files) {
      const newAttachments: AttachmentFile[] = [];
      setIsLoading(true); 
      
      // This composer posts base64 INLINE (no Storage upload), so its ceiling is
      // CLASSIC_INLINE_MAX_TOTAL_BYTES: a Cloud Functions HTTP request is capped
      // at 10 MB on the wire and base64 adds a third. Same wording as the
      // terminal composer, just its own (smaller) numbers. Seeded with the
      // already-attached files so the cap is CUMULATIVE, not per-picker-batch.
      let totalSize = sumAttachmentBytes(attachments);
      const rejected: string[] = [];

      for (const file of Array.from(e.target.files)) {
        const check = checkCanAdd({
          existingBytes: totalSize,
          fileSize: file.size,
          fileName: file.name,
          maxFileBytes: CLASSIC_INLINE_MAX_TOTAL_BYTES,
          maxTotalBytes: CLASSIC_INLINE_MAX_TOTAL_BYTES,
        });
        if (!check.ok) {
          rejected.push(check.kind === 'total' ? `${file.name}: ${check.reason}` : check.reason);
          continue;
        }
        totalSize += file.size;

        try {
          const base64Content = await getFileAsBase64(file);
          newAttachments.push({
            filename: file.name,
            content: base64Content,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
          });
        } catch (err) {
          console.error("Error processing file:", file.name, err);
          rejected.push(`Error processing file ${file.name}`);
        }
      }
      if (rejected.length > 0) {
        setError(`${rejected.join(' · ')}. Remove or shrink an attachment (or share it as a link) and try again.`);
      }
      setAttachments(prev => [...prev, ...newAttachments]);
      setIsLoading(false);
    }
  };
  
  const removeAttachment = (filename: string) => {
    setAttachments(prev => prev.filter(att => att.filename !== filename));
    if (fileInputRef.current) {
        // This doesn't easily remove specific files from the input's FileList
        // but can clear it if all attachments are removed this way
        if (attachments.length === 1) fileInputRef.current.value = "";
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const toEmails = selectedTo.map((opt: EmailOptionType) => opt.value);

    if (!selectedAccountId) {
      setError("Please select a 'From' account.");
      setIsLoading(false);
      return;
    }
    if (toEmails.length === 0) {
      setError("Recipient 'To' field is required.");
      setIsLoading(false);
      return;
    }
    if (!subject.trim()) {
      setError("Subject is required.");
      setIsLoading(false);
      return;
    }
    // Allow sending if either body has content or quoted thread is included
    if (!htmlBody.trim() && !(includeQuotedThread && quotedHtml)) {
      setError("Email body is required.");
      setIsLoading(false);
      return;
    }
    // Validate every recipient is a real address — never send typed-but-unparsed
    // text verbatim as a recipient.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allRecipients = [
      ...toEmails,
      ...selectedCc.map((opt: EmailOptionType) => opt.value),
      ...selectedBcc.map((opt: EmailOptionType) => opt.value),
    ];
    const badRecipient = allRecipients.find((a) => !EMAIL_RE.test(String(a || '').trim()));
    if (badRecipient) {
      setError(`"${badRecipient}" is not a valid email address. Fix or remove it before sending.`);
      setIsLoading(false);
      return;
    }

    // An attachment with neither bytes nor a Storage ref (its content was too
    // large to save with the draft) cannot be sent - say so now instead of
    // letting the server reject it after the send.
    const unsendable = attachments.filter((a) => !a.content && !a.storagePath);
    if (unsendable.length > 0) {
      setError(`${unsendable.map((a) => a.filename).join(', ')}: the file was not saved with this draft - remove it and attach it again before sending.`);
      setIsLoading(false);
      return;
    }

    // Guard the "invoice attached but no PDF" mistake: if the message clearly
    // implies an attached file but none is attached, confirm before sending.
    // A forward carries its originals: not "nothing attached" unless we KNOW
    // the list is empty (or every original was left out).
    const originalsMayAttach = !!forwardSource
      && (forwardOriginalsStatus !== 'loaded' || keptForwardOriginals.length > 0);
    if (attachments.length === 0 && !originalsMayAttach) {
      const bodyText = `${subject} ${htmlBody.replace(/<[^>]*>?/gm, ' ')}`;
      const mentionsAttachment = /\b(attached|attaching|attachment|enclosed|enclosing|pfa|please find|see attached)\b/i.test(bodyText);
      if (mentionsAttachment && !window.confirm('Your message mentions an attachment, but no file is attached. Send anyway?')) {
        setIsLoading(false);
        return;
      }
    }

    // Validation passed — the send owns the content from here. Kill any
    // pending debounce autosave so a save can't land mid/after-send and
    // re-create the draft doc the post-send cleanup deletes.
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    // Build final HTML lazily appending heavy quoted thread at send time
    // IMPORTANT: do NOT run fixImageStyles() on the email body — that helper is meant for signature logos
    // and can distort user-inserted images by forcing fixed width/height attributes.
    let fixedHtmlBody = htmlBody;
    if (includeQuotedThread && quotedHtml) {
      // Ensure a spacing before the quoted section if user typed something
      fixedHtmlBody = (fixedHtmlBody ? fixedHtmlBody + '<br><br>' : '') + quotedHtml;
    }

    // Threading: when this is a reply, pass the ORIGINAL message's Graph id so
    // the backend uses createReply/createReplyAll (sets In-Reply-To/References)
    // and the reply threads in the customer's client.
    const isReplyKind = !!(initialData?.isReply || (initialData as any)?.isReplyAll);
    const replyToGraphId = isReplyKind ? ((initialData as any)?.graph?.id || undefined) : undefined;

    const emailData = {
      accountId: selectedAccountId,
      to: toEmails.length === 1 ? toEmails[0] : toEmails,
      cc: selectedCc.map((opt: EmailOptionType) => opt.value),
      bcc: selectedBcc.map((opt: EmailOptionType) => opt.value),
      subject,
      html: fixedHtmlBody,
      text: fixedHtmlBody.replace(/<[^>]*>?/gm, ''),
      attachments: attachments.length > 0 ? attachments : undefined,
      // Forward: which message's attachments to keep (the server copies them or
      // re-attaches them) and the ones the user left out. Skipped when the
      // original is KNOWN to have none, so a plain forward stays a plain send.
      ...(forwardSource && !(forwardOriginalsStatus === 'loaded' && forwardOriginals.length === 0)
        ? {
            forwardFromEmailId: forwardSource.emailId || undefined,
            forwardFromGraphId: forwardSource.graphId || undefined,
            forwardExcludeNames: forwardExcluded.length > 0 ? forwardExcluded : undefined,
          }
        : {}),
      replyToGraphId,
      replyAll: !!(initialData as any)?.isReplyAll,
      // When sending an auto-drafted recurring-invoice email, the backend marks
      // the linked invoice SENT on success (mirrors the invoice Email button).
      linkedInvoiceId: currentDraft?.linkedInvoiceId || undefined,
    };

    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/sendEmailGraphHttp', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(emailData),
      });
      const data = (await resp.json()) as any;

      if (data?.ok) {
        // Use react-toastify for success notification (the backend returns
        // `graphId`, which isn't meaningful to the user — don't surface "n/a").
        toast.success('Email sent.');
        // A forward whose original could not be found (or only partly copied)
        // still went out - but tell the owner its attachments did not all
        // travel, never let it pass silently.
        const forwardNotes: string[] = Array.isArray(data?.warnings)
          ? data.warnings.filter((w: unknown) => typeof w === 'string' && w)
          : [];
        if (forwardNotes.length > 0) {
          toast.warn(`Sent, but the original's attachments may be incomplete. ${forwardNotes.join(' ')}`, { autoClose: 15000 });
        } else if (data?.forwardAttachments?.mode === 'skipped') {
          toast.warn('Sent, but the original attachments could not be included.', { autoClose: 15000 });
        }

        // Delete the draft on send (Firestore doc + Graph mirror).
        // Marking status:'sent' left the Firestore doc behind and the
        // Graph sync re-mirrored the still-present Outlook draft, so
        // sent emails reappeared in the list. Sent items live in the
        // Sent folder — no separate sent-drafts surface in this app.
        //
        // 2026-05-13 bug fix: dropped the `draftMode === 'edit'` gate
        // so compose-new flows (where auto-save creates a draftId but
        // draftMode stays 'new') also get cleaned up on send. Previously
        // those drafts ghosted into the panel and stayed there forever.
        if (currentDraftId) {
          try {
            await deleteDraftFromContext(currentDraftId);
          } catch (error) {
            console.error('Failed to delete draft on send:', error);
          }
        }

        // Clear form data - ALWAYS
        setSelectedTo([]);
        setSelectedCc([]);
        setSelectedBcc([]);
        setSubject('');
        setHtmlBody('');
        setQuotedHtml('');
        setIncludeQuotedThread(false);
        setAttachments([]);
        // Drop the forward link so the NEXT (unrelated) email composed in this
        // still-mounted composer cannot carry this original's attachments.
        resetForwardState();
        setCurrentDraftId(null);
        setLastSavedContent('');
        setAutoSaveStatus('idle');
        setHasUnsavedChanges(false);

        if (ckeditorRef.current) {
          ckeditorRef.current.setData(''); // Clear CKEditor content
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        onSendSuccess?.(); // Call the callback to close the composer

      } else {
        setError(`Failed to send email: ${data?.error || data?.message || 'Unknown error'}`);
        toast.error(`Failed to send email: ${data?.error || data?.message || 'Unknown error'}`); // Also show error as toast
      }
    } catch (err: any) {
      console.error('Network or other error sending email:', err);
      setError(`An error occurred: ${err.message}. Please check console for details.`);
      toast.error(`An error occurred: ${err.message}`); // Also show catch error as toast
    }
    setIsLoading(false);
  };

  // Helper to convert string/array of strings to EmailOptionType[] for pre-filling
  // This might need adjustment if pre-fill data doesn't have associated names
  const toEmailOptions = (emails?: string | string[], names?: string | string[]): EmailOptionType[] => {
    if (!emails) return [];
    const emailArray = Array.isArray(emails) ? emails : emails.split(',').map(e => e.trim()).filter(e => e);
    const nameArray = names ? (Array.isArray(names) ? names : names.split(',').map(n => n.trim())) : [];
    
    return emailArray.map((e, index) => {
        const name = nameArray[index] || e.split('@')[0]; // Fallback label part from email username
        return { value: e, label: `${name} <${e}>` };
    });
  };

  // Custom styles for react-select to better match Metronic form inputs
  const compactSelectStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '8px',
        // Visible focus ring (terracotta tint under graphite) so the active
        // To/Cc/Bcc field is obvious — matches the Subject .form-control.
        boxShadow: state.isFocused ? '0 0 0 3px var(--bs-primary-bg-subtle)' : 'none',
        minHeight: '30px',
        '&:hover': {
            border: 'none',
        }
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: '0'
    }),
    input: (provided: any) => ({ ...provided, color: 'var(--bs-input-color)', margin: '0px' }),
    menu: (provided: any) => ({ ...provided, backgroundColor: 'var(--bs-body-bg)', zIndex: 1070 }),
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'var(--bs-primary)' : state.isFocused ? 'var(--bs-primary-bg-subtle, var(--bs-light-primary))' : 'var(--bs-body-bg)',
        color: state.isSelected ? 'var(--bs-white)' : 'var(--bs-input-color)',
        '&:active': { backgroundColor: 'var(--bs-primary)' }
    }),
    multiValue: (provided: any) => ({ 
      ...provided, 
      backgroundColor: 'var(--bs-primary-bg-subtle, var(--bs-light-primary))',
      borderRadius: '8px'
    }),
    multiValueLabel: (provided: any) => ({ 
      ...provided, 
      color: 'var(--bs-primary-text-emphasis, var(--bs-primary))', 
      padding: '2px 4px', 
      fontSize: '90%',
      fontWeight: 600
    }),
    multiValueRemove: (provided: any) => ({
        ...provided,
        color: 'var(--bs-primary-text-emphasis, var(--bs-primary))',
        '&:hover': { backgroundColor: 'var(--bs-danger)', color: 'var(--bs-white)' }
    }),
    placeholder: (provided: any) => ({ ...provided, padding: '0px' }),
    indicatorsContainer: (provided: any) => ({ ...provided, display: 'none' }), // Hide dropdown indicator
  };

  // Insert Signature handler for CKEditor 5
  const handleInsertSignature = () => {
    if (ckeditorRef.current && signatureHtml) {
      const editor = ckeditorRef.current;
      // Fix image styles in signature before inserting
      const fixedSignatureHtml = fixImageStyles(signatureHtml);
      const viewFragment = editor.data.processor.toView(fixedSignatureHtml);
      const modelFragment = editor.data.toModel(viewFragment);
      editor.model.insertContent(modelFragment, editor.model.document.selection);
    }
  };

  // Auto-insert signature for new emails
  const autoInsertSignature = (htmlContent: string = '') => {
    if (signatureHtml) {
      const fixedSignatureHtml = fixImageStyles(signatureHtml);
      // Add signature at the end with some spacing
      return htmlContent + (htmlContent ? '<br><br>' : '') + fixedSignatureHtml;
    }
    return htmlContent;
  };

  // Main component render - no surrounding card, just the form content
  return (
    <form
      onSubmit={handleSubmit}
      className='form d-flex flex-column h-100'
      style={{
        height: '100%',
        minHeight: 0
      }}
    >
      {/* Live-update banner — shown only when Andrei edited this draft while the
          user has UNSAVED local edits, so their typing is never overwritten. */}
      {pendingExternalUpdate && (
        <div
          className='d-flex align-items-center justify-content-between'
          style={{ padding: '8px 25px', background: 'rgba(251,191,36,0.12)', borderBottom: '1px solid rgba(251,191,36,0.4)', fontSize: 13 }}
        >
          <span>Andrei updated this draft. Loading it will replace your unsaved changes.</span>
          <span className='d-flex gap-3'>
            <button type='button' className='btn btn-sm btn-warning py-1' onClick={() => applyExternalDraft(pendingExternalUpdate)}>Load update</button>
            <button type='button' className='btn btn-sm btn-link text-muted py-1' onClick={() => setPendingExternalUpdate(null)}>Keep mine</button>
          </span>
        </div>
      )}
      {/* Header with From, To, CC, Subject and action buttons */}
      <div className='border-bottom' style={{ padding: '12px 25px' }}>
        <div className='d-flex align-items-center mb-3'>
            <div className='d-flex align-items-center' style={{flex: '1 1 50%'}}>
                <label className='fw-semibold text-body me-2'>From:</label>
                <div className='flex-grow-1'>
                    {isFetchingAccounts ? <span className='form-control form-control-sm form-control-solid'>Loading...</span> : (
                        <select
                            name='accountId'
                            className='form-select form-select-sm form-select-solid border-0'
                            value={selectedAccountId}
                            onChange={(e) => setSelectedAccountId(e.target.value)}
                            disabled={senderAccounts.length === 0 || isLoading}
                        >
                            {senderAccounts.length === 0 && <option value=''>No accounts</option>}
                            {senderAccounts.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
            <div className='d-flex align-items-center ms-auto flex-wrap'>
                <button
                    type="button"
                    className="btn btn-sm btn-icon btn-light-primary me-2"
                    onClick={handleInsertSignature}
                    disabled={!signatureHtml || isLoading}
                    title={!signatureHtml ? "No signature set for this account" : "Insert signature"}
                >
                    <i className='fas fa-signature'></i>
                </button>
                <input
                    type='file'
                    multiple
                    ref={fileInputRef}
                    className='d-none' // Hide the actual input
                    onChange={handleFileChange}
                    disabled={isLoading}
                />
                <button 
                    type='button' 
                    className='btn btn-sm btn-icon btn-light me-2' 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    title={`Attach files (max ${formatLimit(CLASSIC_INLINE_MAX_TOTAL_BYTES)} total)`}
                >
                    <KTSVG path='/media/icons/duotune/files/fil022.svg' className='svg-icon-2' />
                </button>
                {/* Auto-save status indicator — near the send button, subtle */}
                <div
                  className='d-flex align-items-center me-3'
                  style={{
                    fontSize: '0.78rem',
                    minWidth: '110px',
                    transition: 'opacity 0.6s ease',
                    opacity: autoSaveStatus === 'idle' && !hasUnsavedChanges ? 0 : 1,
                    pointerEvents: 'none',
                  }}
                >
                  {autoSaveStatus === 'saving' && (
                    <>
                      <span className='spinner-border spinner-border-sm text-muted me-1' style={{width: '0.7rem', height: '0.7rem'}}></span>
                      <span className='text-muted'>Saving draft…</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className='text-success d-flex align-items-center' style={{gap: '4px'}}>
                      <i className='bi bi-check-circle-fill' style={{fontSize: '0.8rem'}}></i>
                      Draft saved
                    </span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className='text-warning d-flex align-items-center' style={{gap: '4px'}}>
                      <i className='bi bi-exclamation-triangle-fill' style={{fontSize: '0.8rem'}}></i>
                      Save failed
                    </span>
                  )}
                  {autoSaveStatus === 'idle' && hasUnsavedChanges && (
                    <span className='text-muted'>Unsaved changes</span>
                  )}
                </div>

                {/* Manual save button - always visible when composing */}
                <button
                  type="button"
                  className="btn btn-sm btn-light me-2"
                  onClick={triggerManualSave}
                  disabled={isLoading || autoSaveStatus === 'saving'}
                  title="Save draft manually"
                >
                  <KTSVG path='/media/icons/duotune/general/gen016.svg' className='svg-icon-3 me-1' />
                  Save Draft
                </button>

                <button type='submit' className='btn btn-sm btn-primary ms-3' disabled={isLoading || isFetchingAccounts}>
                    {!isLoading && 'Send'}
                    {isLoading && <span className='spinner-border spinner-border-sm'></span>}
                </button>
            </div>
        </div>

        <div className='mb-0'>
            {/* To field row — always visible, with +CC/BCC toggle */}
            <div className='d-flex align-items-center border-top pt-2 mb-2'>
                <div className='d-flex align-items-center flex-fill'>
                    <label className='fw-semibold text-body me-2' style={{width: '25px'}}>To</label>
                    <div className='flex-grow-1'>
                        <CreatableSelect<EmailOptionType, true, any>
                            isMulti
                            name="to"
                            options={clientEmailOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={selectedTo}
                            onChange={(selectedOptions: OnChangeValue<EmailOptionType, true>) => setSelectedTo(selectedOptions || [])}
                            isLoading={isFetchingClients}
                            isDisabled={isLoading}
                            placeholder=""
                            styles={compactSelectStyles}
                        />
                    </div>
                </div>
                {/* Toggle CC/BCC visibility */}
                {!showCcBcc && (
                  <button
                    type='button'
                    className='btn btn-sm btn-link text-muted ms-2 p-0'
                    style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}}
                    onClick={() => setShowCcBcc(true)}
                    title='Show CC and BCC fields'
                  >
                    + CC / BCC
                  </button>
                )}
            </div>

            {/* CC and BCC fields — collapsed by default, expanded when showCcBcc or when they have values */}
            {showCcBcc && (
              <>
                <div className='d-flex align-items-center border-top pt-2 mb-2'>
                    <div className='d-flex align-items-center flex-fill'>
                        <label className='fw-semibold text-body me-2' style={{width: '25px'}}>Cc</label>
                        <div className='flex-grow-1'>
                            <CreatableSelect<EmailOptionType, true, any>
                                isMulti
                                name="cc"
                                options={clientEmailOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                value={selectedCc}
                                onChange={(selectedOptions: OnChangeValue<EmailOptionType, true>) => setSelectedCc(selectedOptions || [])}
                                isLoading={isFetchingClients}
                                isDisabled={isLoading}
                                placeholder=""
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>
                </div>

                <div className='d-flex align-items-center border-top pt-2 mb-2'>
                    <div className='d-flex align-items-center flex-fill'>
                        <label className='fw-semibold text-body me-2' style={{width: '25px'}}>Bcc</label>
                        <div className='flex-grow-1'>
                            <CreatableSelect<EmailOptionType, true, any>
                                isMulti
                                name="bcc"
                                options={clientEmailOptions}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                value={selectedBcc}
                                onChange={(selectedOptions: OnChangeValue<EmailOptionType, true>) => setSelectedBcc(selectedOptions || [])}
                                isLoading={isFetchingClients}
                                isDisabled={isLoading}
                                placeholder=""
                                styles={compactSelectStyles}
                            />
                        </div>
                    </div>
                    {/* Collapse button in the BCC row */}
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-muted ms-2 p-0'
                      style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}}
                      onClick={() => setShowCcBcc(false)}
                      title='Hide CC and BCC fields'
                    >
                      − CC / BCC
                    </button>
                </div>
              </>
            )}

            <div className='d-flex align-items-center border-top pt-2'>
                 <input
                    type='text'
                    name='subject'
                    className='form-control form-control-sm form-control-solid border-0 px-0' // Flattened input
                    placeholder='Subject'
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={isLoading}
                />
            </div>
        </div>
      </div>
      
      {error && <div className='alert alert-danger rounded-0 m-0 p-3 fs-7'>{error}</div>}

      {/* Quoted thread controls and preview (kept outside editor for performance) */}
      {(quotedHtml || (initialData?.isReply || initialData?.isForward)) && (
        <div className='border-bottom d-flex align-items-center justify-content-between' style={{ padding: '8px 25px' }}>
          <div className='form-check form-check-sm form-check-custom'>
            <input
              className='form-check-input'
              type='checkbox'
              id='includeQuotedThread'
              checked={includeQuotedThread}
              onChange={(e) => setIncludeQuotedThread(e.target.checked)}
              disabled={isLoading}
            />
            <label className='form-check-label ms-2' htmlFor='includeQuotedThread'>Include quoted thread</label>
          </div>
          {quotedHtml && (
            <button
              type='button'
              className='btn btn-link btn-sm'
              onClick={() => setShowQuotedPreview(prev => !prev)}
              disabled={!includeQuotedThread}
            >
              {showQuotedPreview ? 'Hide preview' : 'Show preview'}
            </button>
          )}
        </div>
      )}

      {/* Attachment List */}
      {(attachments.length > 0 || keptForwardOriginals.length > 0 || forwardExcluded.length > 0
        || (forwardSource !== null && forwardOriginalsStatus === 'unavailable')) && (
          <div className='border-bottom' style={{ padding: '8px 25px' }}>
            {attachments.length > 0 && (
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='fs-7 text-muted'>Attachments ({attachments.length})</span>
                <span className='fs-7 text-muted'>
                  Total size: {formatTotalIndicator(sumAttachmentBytes(attachments), CLASSIC_INLINE_MAX_TOTAL_BYTES)}
                </span>
              </div>
            )}
            <div className='d-flex flex-wrap gap-2'>
              {attachments.map((att, index) => (
                <div key={index} className='d-flex align-items-center p-1 bg-body-secondary rounded border'>
                  <span className='fs-7 text-truncate' style={{maxWidth: '150px'}}>{att.filename}</span>
                  <span className={`fs-8 ms-1 ${att.content || att.storagePath ? 'text-muted' : 'text-danger'}`}>
                    ({att.content || att.storagePath ? formatBytes(attachmentBytes(att)) : 'not saved with this draft'})
                  </span>
                  <button type='button' className='btn btn-sm btn-icon btn-light-danger ms-1' onClick={() => removeAttachment(att.filename)} title='Remove' disabled={isLoading}>
                    <i className='fas fa-times'></i>
                  </button>
                </div>
              ))}
              {/* Forward: the original's attachments (the server carries them over).
                  Read-only apart from the x, which tells it to leave one out. */}
              {keptForwardOriginals.map((o, i) => (
                <div
                  key={`orig:${i}:${o.name}`}
                  className='d-flex align-items-center p-1 bg-body-secondary rounded border'
                  title={`${o.name} - from the original message`}
                >
                  <span className='badge badge-light-info fs-9 me-1'>from original</span>
                  <span className='fs-7 text-truncate' style={{maxWidth: '150px'}}>{o.name}</span>
                  {o.size !== null && <span className='fs-8 text-muted ms-1'>({formatBytes(o.size)})</span>}
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light-danger ms-1'
                    onClick={() => excludeForwardOriginal(o.name)}
                    title='Leave out of the forward'
                    aria-label={`Leave ${o.name} out of the forward`}
                    disabled={isLoading}
                  >
                    <i className='fas fa-times'></i>
                  </button>
                </div>
              ))}
            </div>
            {forwardExcluded.length > 0 && (
              <div className='fs-8 text-muted mt-2'>
                {forwardExcluded.length} original file{forwardExcluded.length !== 1 ? 's' : ''} left out
                {' '}&middot;{' '}
                <button type='button' className='btn btn-link btn-sm p-0 fs-8' onClick={() => setForwardExcluded([])} disabled={isLoading}>restore</button>
              </div>
            )}
            {forwardSource !== null && forwardOriginalsStatus === 'unavailable' && (
              <div className='fs-8 text-muted mt-2'>The original's attachments will be included if available.</div>
            )}
          </div>
      )}

      {/* Body (CKEditor) */}
      <div className='flex-grow-1 d-flex flex-column' style={{minHeight: '200px', overflow: 'hidden'}}>
          <style>{`
            /* Disable CKEditor sticky toolbar in email composer to prevent floating issues */
            .ck-editor-container .ck-sticky-panel__content_sticky {
              position: relative !important;
              top: 0 !important;
              width: auto !important;
              margin-left: 0 !important;
            }
            .ck-editor-container .ck-sticky-panel__placeholder {
              display: none !important;
            }

            /* Theme-safe CKEditor colors (avoid hard-coded light backgrounds / dark text) */
            .ck-editor-container .ck.ck-toolbar {
              background: var(--bs-body-bg) !important;
              color: var(--bs-body-color) !important;
              border-color: var(--bs-border-color) !important;
            }
            .ck-editor-container .ck.ck-toolbar .ck-button,
            .ck-editor-container .ck.ck-toolbar .ck-button .ck-icon {
              color: var(--bs-body-color) !important;
            }
            .ck-editor-container .ck.ck-editor__main > .ck-editor__editable {
              background: var(--bs-body-bg) !important;
              color: var(--bs-body-color) !important;
              border-color: var(--bs-border-color) !important;
            }
            .ck-editor-container .ck.ck-editor__main > .ck-editor__editable:focus {
              box-shadow: none !important;
            }
            .ck-editor-container .ck-content {
              color: var(--bs-body-color) !important;
            }
            .ck-editor-container .ck-content a {
              color: var(--bs-link-color) !important;
            }

            /* Prevent image distortion in the composer:
               - Keep images responsive
               - Preserve aspect ratio even if width/height attributes exist */
            .ck-editor-container .ck-content img {
              max-width: 100% !important;
              height: auto !important;
              width: auto;
            }
            .ck-editor-container .ck-content figure.image {
              max-width: 100%;
            }
            .ck-editor-container .ck-content figure.image img {
              height: auto !important;
            }
            .ck-editor-container .ck.ck-dropdown__panel {
              background: var(--bs-body-bg) !important;
              color: var(--bs-body-color) !important;
              border-color: var(--bs-border-color) !important;
            }

            /* Composer full width: no extra box around toolbar + editor */
            .ck-editor-container .ck-editor {
              border: none !important;
              border-radius: 0 !important;
              overflow: hidden !important;
              background: var(--bs-body-bg) !important;
            }
            /* 2. Remove ALL borders from inner elements */
            .ck-editor-container .ck-editor__top,
            .ck-editor-container .ck-editor__main,
            .ck-editor-container .ck-toolbar,
            .ck-editor-container .ck-toolbar_grouping,
            .ck-editor-container .ck-sticky-panel,
            .ck-editor-container .ck-sticky-panel__content,
            .ck-editor-container .ck-editor__editable,
            .ck-editor-container .ck-editor__editable_inline,
            .ck-editor-container .ck.ck-toolbar,
            .ck-editor-container .ck.ck-editor__top,
            .ck-editor-container .ck.ck-editor__main,
            .ck-editor-container .ck.ck-editor__editable {
              border: none !important;
              border-radius: 0 !important;
              box-shadow: none !important;
            }
            /* 3. Add ONLY a bottom divider line under toolbar */
            .ck-editor-container .ck-editor__top {
              border-bottom: 1px solid var(--bs-border-color) !important;
            }
            /* 4. Override CKEditor rounded corners class */
            .ck-editor-container .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
            .ck-editor-container .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
              border: none !important;
              border-radius: 0 !important;
            }
            .ck-editor-container .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
            .ck-editor-container .ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
              border: none !important;
              border-radius: 0 !important;
            }

            /* Better layout/scrolling: toolbar fixed within composer, editable fills remaining height */
            .ck-editor-container {
              display: flex;
              flex-direction: column;
              min-height: 240px;
              min-height: 0; /* allow flex children to size correctly */
            }
            .ck-editor-container .ck.ck-editor {
              display: flex;
              flex-direction: column;
              flex: 1 1 auto;
              min-height: 0;
              height: 100%;
            }
            .ck-editor-container .ck.ck-toolbar {
              flex: 0 0 auto;
              position: sticky;
              top: 0;
              z-index: 2;
            }
            .ck-editor-container .ck.ck-editor__main {
              flex: 1 1 auto;
              min-height: 0;
            }
            .ck-editor-container .ck.ck-editor__main > .ck-editor__editable {
              min-height: 0;
              height: 100%;
              padding: 18px 25px !important;
              overflow-y: auto;
            }
            .ck-editor-container .ck.ck-editor__editable::placeholder {
              color: var(--bs-secondary-color) !important;
            }

            /* Mobile: make toolbar easier to use without clipping */
            @media (max-width: 575px) {
              .ck-editor-container .ck.ck-toolbar__items {
                flex-wrap: wrap;
              }
              .ck-editor-container .ck.ck-toolbar .ck-button {
                padding: 6px !important;
              }
            }
          `}</style>
          <div className='ck-editor-container flex-grow-1' style={{ height: '100%', padding: 0, minHeight: 0 }}>
            {isEditorReady ? (
              <CKEditor
                  editor={ClassicEditor}
                  config={{
                  licenseKey: 'GPL',
                  placeholder: 'Type or paste your email here...',
                  extraPlugins: [FirebaseUploadAdapterPlugin], // Add the Firebase upload adapter
                  // Disable sticky toolbar to prevent positioning issues in email panel
                  toolbar: {
                    // Keep the toolbar compact + predictable
                    items: [
                      'undo', 'redo',
                      '|',
                      'heading',
                      '|',
                      'bold', 'italic', 'link',
                      '|',
                      'bulletedList', 'numberedList',
                      '|',
                      'outdent', 'indent',
                      '|',
                      'blockQuote',
                      'insertTable',
                      'imageUpload',
                    ],
                    shouldNotGroupWhenFull: true,
                    viewportTopOffset: 0,
                  },
                  ui: {
                    viewportOffset: {
                      top: 0
                    }
                  }
                  }}
                  data={htmlBody}
                  onChange={(_, editor) => setHtmlBody(editor.getData())}
                  disabled={isLoading}
                  onReady={editor => { ckeditorRef.current = editor; }}
              />
            ) : (
              /* Loading skeleton — shown while the 450ms animation delay completes */
              <div style={{ padding: '12px 25px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Simulated toolbar skeleton */}
                <div style={{
                  height: '36px',
                  borderRadius: '4px',
                  background: 'var(--bs-secondary-bg, #e9ecef)',
                  animation: 'skeleton-pulse 1.4s ease-in-out infinite',
                  opacity: 0.6,
                }} />
                {/* Simulated text line skeletons */}
                {[85, 72, 90, 60].map((w, i) => (
                  <div key={i} style={{
                    height: '14px',
                    width: `${w}%`,
                    borderRadius: '4px',
                    background: 'var(--bs-secondary-bg, #e9ecef)',
                    animation: `skeleton-pulse 1.4s ease-in-out ${i * 0.1}s infinite`,
                    opacity: 0.5,
                  }} />
                ))}
                <style>{`
                  @keyframes skeleton-pulse {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 0.7; }
                  }
                `}</style>
              </div>
            )}
          </div>
      </div>

      {/* Quoted thread preview moved below the editor. quotedHtml is already
          DOMPurify-sanitized (via cleanEmailHtmlForReply); render it in a
          fully sandboxed iframe so even a sanitizer bypass cannot execute
          script or reach the parent page / Firebase session. */}
      {showQuotedPreview && includeQuotedThread && quotedHtml && (
        <div style={{ padding: 25 }}>
          <div className='border rounded bg-body-secondary p-3 text-body' style={{maxHeight: '240px', overflowY: 'auto'}}>
            <iframe
              title='Quoted thread preview'
              sandbox=''
              srcDoc={quotedHtml}
              style={{ width: '100%', minHeight: '200px', border: 'none', background: 'transparent' }}
            />
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && (
        <div className="modal fade show" style={{ display: "block", zIndex: 1060 }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Unsaved Changes</h5>
                <button type="button" className="btn-close" onClick={() => setShowUnsavedChangesModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>You have unsaved changes in your email draft. What would you like to do?</p>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Saving will create or update your draft. You can continue editing it later.
                </div>
              </div>
              <div className="modal-footer">
                {/* Primary action first — most prominent */}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveBeforeClose}
                  disabled={autoSaveStatus === 'saving'}
                >
                  {autoSaveStatus === 'saving' ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Draft'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUnsavedChangesModal(false)}
                >
                  Continue Editing
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDiscardChanges}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showUnsavedChangesModal && <div className="modal-backdrop fade show" style={{zIndex: 1055}}></div>}
    </form>
  );
});

SendEmailForm.displayName = 'SendEmailForm';

export default SendEmailForm; 